import { ApplicationLayout } from '@folio/components';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  type ActionsEnumKey,
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  emailAccountControllerListMineQueryOptions,
  generalInboxControllerGetSummaryQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
  viewSpecificControllerListBrokerageFirmDerogationsQueryOptions,
  viewSpecificControllerListBrokerageFirmPoliciesQueryOptions,
} from 'korint-sdk';
import type { IntermediaryPage, IntermediaryPageType } from 'korint-sdk/react';
import {
  ArchiveIcon,
  Banknote,
  BarChart2,
  BotMessageSquare,
  Building2,
  CheckCircle,
  FileWarning,
  FolderIcon,
  Folders,
  HomeIcon,
  Inbox,
  InfoIcon,
  Mail,
  PackageIcon,
  ShieldAlert,
  Users,
  WalletIcon,
} from 'lucide-react';
import { useFeatureFlag, useKorintConfig } from 'korint-sdk/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { DEROGATION_TO_VALIDATE_STATUSES } from '../modules/DerogationValidationsModule/DerogationValidationsModule';
import {
  FLAGGED_STATUSES,
  FRAUD_CONTROL_VIEWS,
} from '../modules/FraudControlModule/FraudControlModule';

export function getIntermediaryPageMeta(
  page: IntermediaryPage,
  t: (key: string) => string,
): { icon: React.ReactNode; label: string } {
  switch (page.type) {
    case 'custom':
      return { icon: page.icon, label: t(page.name) };
    case 'ongoing-new-business':
      return {
        icon: <HomeIcon className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.ongoingNewBusiness'),
      };
    case 'archived-new-business':
      return {
        icon: <ArchiveIcon className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.archivedNewBusiness'),
      };
    case 'portfolio':
      return {
        icon: <WalletIcon className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.portfolio'),
      };
    case 'reports':
      return {
        icon: <FolderIcon className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.reports'),
      };
    case 'commissions':
      return {
        icon: <Banknote className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.commissions'),
      };
    case 'intermediary-user-list':
      return {
        icon: <Users className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.users'),
      };
    case 'intermediary-user-form':
      return {
        icon: <Users className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.userForm'),
      };
    case 'brokerage-firm-list':
      return {
        icon: <Building2 className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.brokerageFirms'),
      };
    case 'my-brokerage-firm':
      return {
        icon: <InfoIcon className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.myBrokerageFirm'),
      };
    case 'accounting':
      return {
        icon: <Folders className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.accounting'),
      };
    case 'derogations':
      return {
        icon: <CheckCircle className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.derogations'),
      };
    case 'products':
      return {
        icon: <PackageIcon className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.products'),
      };
    case 'analytics':
      return {
        icon: <BarChart2 className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.analytics'),
      };
    case 'conversations':
      return {
        icon: <BotMessageSquare className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.korintAI'),
      };
    case 'general-inbox':
      return {
        icon: <Inbox className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.generalInbox'),
      };
    case 'claims':
      return {
        icon: <FileWarning className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.claims'),
      };
    case 'fraud-control':
      return {
        icon: <ShieldAlert className="size-4" />,
        label: t(page.name ?? 'sidebar.intermediary.fraudControl'),
      };
  }
}

// Email pages are absent: their endpoints skip authorization and check the caller themselves.
const PAGE_TYPE_REQUIRED_ACTION: Partial<Record<IntermediaryPageType, ActionsEnumKey>> = {
  'ongoing-new-business': 'LIST_ALL_POLICIES_FROM_BROKERAGE_FIRM',
  'archived-new-business': 'LIST_ALL_POLICIES_FROM_BROKERAGE_FIRM',
  portfolio: 'LIST_LIVE_POLICIES_FROM_BROKERAGE_FIRM',
  reports: 'GET_REPORT',
  'intermediary-user-list': 'LIST_BROKERS',
  'intermediary-user-form': 'INVITE_BROKER',
  accounting: 'GET_ACCOUNTING_SUMMARY_FOR_BROKERAGE_FIRM',
  derogations: 'LIST_BROKERAGE_FIRM_DEROGATIONS',
  'brokerage-firm-list': 'LIST_CHILD_BROKERAGE_FIRMS',
  'my-brokerage-firm': 'GET_BROKERAGE_FIRM',
  products: 'LIST_PRODUCT_DOCUMENTS',
  analytics: 'GET_METABASE_TOKEN',
  conversations: 'CREATE_CONVERSATION',
  commissions: 'GET_REPORT',
  claims: 'LIST_CLAIMS_MANAGEMENT',
  'fraud-control': 'GET_POLICY_DOCUMENT_FRAUD',
};

// Statements are only ever issued between a wholesale firm and its retail firms.
const COMMISSIONS_FIRM_TYPES = ['WHOLESALE', 'RETAIL'];

export function IntermediaryDashboardLayout({
  pages,
  logo,
}: {
  pages: ReadonlyArray<IntermediaryPage>;
  logo: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { korintConfig } = useKorintConfig();
  const { data: user } = useSuspenseQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const brokerageFirmId = user.brokerageFirmId!;

  const {
    data: { firmType },
  } = useSuspenseQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId),
  });

  const { can, isLoading: arePermissionsLoading } = useUserPermissions({
    actions: pages.flatMap(page =>
      page.type !== 'custom' && PAGE_TYPE_REQUIRED_ACTION[page.type]
        ? [PAGE_TYPE_REQUIRED_ACTION[page.type]!]
        : [],
    ),
  });

  const connectedEmailAccountsEnabled = useFeatureFlag('feat_connected_email_accounts');
  const commissionStatementsEnabled = useFeatureFlag('feat_commission_statements');
  const policyFraudDetectionEnabled = useFeatureFlag('feat_policy_fraud_detection');
  const hasEmailAccountsPage =
    connectedEmailAccountsEnabled && pages.some(page => page.type === 'general-inbox');

  const { data: emailAccounts, refetch: refetchEmailAccounts } = useQuery({
    ...emailAccountControllerListMineQueryOptions(),
    enabled: hasEmailAccountsPage,
    select: data => ({
      errorAccountCount: data.accounts.filter(({ isOwn, status }) => isOwn && status === 'error')
        .length,
    }),
  });
  const errorAccountCount = emailAccounts?.errorAccountCount ?? 0;

  const { data: unreadCount = 0, refetch: refetchUnreadCount } = useQuery({
    ...generalInboxControllerGetSummaryQueryOptions(),
    enabled: hasEmailAccountsPage,
    select: data => data.unreadThreadCount,
  });

  const visiblePages = pages
    .filter(page => !page.forTypes || page.forTypes.includes(firmType))
    .filter(page => page.type !== 'general-inbox' || connectedEmailAccountsEnabled)
    .filter(page => page.type !== 'fraud-control' || policyFraudDetectionEnabled)
    .filter(
      page =>
        page.type !== 'commissions' ||
        (commissionStatementsEnabled && COMMISSIONS_FIRM_TYPES.includes(firmType)),
    )
    .filter(page => {
      if (page.type === 'custom') return true;
      const action = PAGE_TYPE_REQUIRED_ACTION[page.type];
      return !action || can[action];
    });

  const hasDerogationsPage = visiblePages.some(page => page.type === 'derogations');

  // count: 1 — only totalCount is read, the page itself is thrown away.
  const { data: derogationsToValidateCount, refetch: refetchDerogationsCount } = useQuery({
    ...viewSpecificControllerListBrokerageFirmDerogationsQueryOptions(
      brokerageFirmId,
      { statuses: DEROGATION_TO_VALIDATE_STATUSES },
      { page: 1, count: 1 },
    ),
    enabled: hasDerogationsPage,
    select: data => data.totalCount,
  });

  const hasFraudControlPage = visiblePages.some(page => page.type === 'fraud-control');

  const flaggedCountQuery = (view: (typeof FRAUD_CONTROL_VIEWS)[number]) => {
    const baseFilters = korintConfig.modulesConfig?.[view]?.baseFilters;
    return {
      ...viewSpecificControllerListBrokerageFirmPoliciesQueryOptions(brokerageFirmId, {
        viewType: 'live' as const,
        ...baseFilters,
        fraudStatuses: FLAGGED_STATUSES,
        pageInfo: { page: 1, count: 1 },
      }),
      enabled: hasFraudControlPage && !!baseFilters,
      select: (data: { totalCount: number }) => data.totalCount,
    };
  };
  const { data: flaggedPortfolioCount = 0, refetch: refetchFlaggedPortfolioCount } = useQuery(
    flaggedCountQuery('portfolio'),
  );
  const { data: flaggedNewBusinessCount = 0, refetch: refetchFlaggedNewBusinessCount } = useQuery(
    flaggedCountQuery('ongoingNewBusiness'),
  );
  const flaggedPolicyCount = flaggedPortfolioCount + flaggedNewBusinessCount;

  // The sidebar outlives every navigation, so without this the counts never refresh on their own.
  const { pathname } = useLocation();
  useEffect(() => {
    if (hasEmailAccountsPage) {
      refetchEmailAccounts();
      refetchUnreadCount();
    }
    if (hasDerogationsPage) refetchDerogationsCount();
    if (hasFraudControlPage) {
      refetchFlaggedPortfolioCount();
      refetchFlaggedNewBusinessCount();
    }
  }, [
    pathname,
    hasEmailAccountsPage,
    refetchEmailAccounts,
    refetchUnreadCount,
    hasDerogationsPage,
    refetchDerogationsCount,
    hasFraudControlPage,
    refetchFlaggedPortfolioCount,
    refetchFlaggedNewBusinessCount,
  ]);

  const items = visiblePages.map(page => {
    if (page.type === 'general-inbox') {
      return {
        label: t('sidebar.intermediary.emails'),
        icon: <Mail className="size-4" />,
        link: page.type,
        badgeCount: unreadCount > 0 ? unreadCount : undefined,
        badgeLabel: t('sidebar.intermediary.generalInboxBadge', { count: unreadCount }),
        warningLabel:
          errorAccountCount > 0 ? t('sidebar.intermediary.emailAccountsError') : undefined,
      };
    }

    const badgeCount =
      page.type === 'derogations' && derogationsToValidateCount
        ? derogationsToValidateCount
        : page.type === 'fraud-control' && flaggedPolicyCount
          ? flaggedPolicyCount
          : undefined;

    const link = page.type === 'custom' ? page.path : page.type;

    return { ...getIntermediaryPageMeta(page, t), link, badgeCount };
  });

  return (
    <ApplicationLayout
      items={arePermissionsLoading ? [] : items}
      logo={
        <div>
          {/* This extra div is used to have the dynamic width of the logo */}
          {logo}
        </div>
      }
    />
  );
}
